import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams } from "react-router-dom";
import { decodeToken, getToken } from "../../utils/TokenUtils";
import { API_URL, ENDPOINTS } from "../../constants/api";
import axios from 'axios';

const CustomRegistrationLetter = () => {
  const { id, route } = useParams();
  const token = getToken();
  const banner = decodeToken(token, "banner");
  const user_id = decodeToken(token, "id");
  const username = decodeToken(token, "name");
  const [header, setHeader] = useState({});
  const [data, setData] = useState([]);
  const [info, setInfo] = useState({});
  const [footer, setFooter] = useState({});

  useEffect(() => {
    const url = `${API_URL}/${ENDPOINTS.SYS}/activityReport/${route}`;
    const fetchData = async () => {
      try {
        const response = await axios.post(url, { student_id: id, user_id }, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        const resData = response.data.data;
        console.log("res: ", resData);
        setHeader(resData?.find((d) => d.title === 'header')?.fields || {});
        setData([resData?.find((d) => d.title === 'data').fields] || []);
        setInfo(resData?.find((d) => d.title === 'info')?.fields || {});
        setFooter(resData?.find((d) => d.title === 'footer')?.fields || {});
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [route, id, user_id, token]);

  useEffect(() => {
    if (data.length <= 0) return;
    const timer = setTimeout(() => {
      window.print();
    }, 1000); 
    return () => clearTimeout(timer);
  }, [data]);

  if (data.length <= 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading Document...</span>
        </div>
      </div>
    );
  }

  // DYNAMIC SELECTION: Extract the first 4 fields automatically from the SQL data payload
  const rawKeys = data[0] ? Object.keys(data[0]) : [];
  const topSectionKeys = rawKeys
    .filter((key) => !key.startsWith("_") && key !== "student_image" && key !== "title")
    .slice(0, 4);

  return (
    <>
      {/* Strict Print Overrides */}
      <style>{`
        @media print {
          body { background: #fff; margin: 0; padding: 0; }
          .print-letter-container { 
            box-shadow: none !important; 
            border: 2px solid #0f172a !important;
            margin: 0 !important;
            padding: 5mm !important;
          }
          .info-card {
            background-color: #f8fafc !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        @page {
          margin: 0;
        }
      `}</style>

      <div 
        className="print-letter-container"
        style={{
          width: "210mm",
          height: "297mm", 
          margin: "5px auto",
          fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          backgroundColor: "#fff",
          padding: "5mm",
          boxShadow: "0 4px 25px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }}
      >
        <div>
          {/* Institutional Header Banner */}
          <div className="text-center mb-4">
            <img
              src={`${API_URL}/${banner}`}
              alt="Institution Header"
              style={{ width: "100%", maxHeight: "110px", objectFit: "contain" }}
            />
          </div>

          {/* Reference Block */}
          <div className="d-flex justify-content-between align-items-center mb-4" style={{ borderBottom: "2px solid #0f172a", paddingBottom: "10px" }}>
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#334155", letterSpacing: "0.5px" }}>
              REF NO: <span style={{ color: "#0f172a" }}>REG-{id || "000"}</span>
            </span>
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#334155", letterSpacing: "0.5px" }}>
              DATE: <span style={{ color: "#0f172a" }}>{new Date().toLocaleDateString("en-GB")}</span>
            </span>
          </div>

          {/* Document Title */}
          <div className="text-center my-4">
            <h3 style={{ 
              fontWeight: "800", 
              textTransform: "uppercase", 
              letterSpacing: "2px",
              color: "#0f172a",
              fontSize: "22px",
              margin: 0
            }}>
              {header?.link}
            </h3>
          </div>

          {/* Layout Partition Map */}
          {data.map((item, index) => {
            const allEntries = Object.entries(item).filter(
              ([key]) => !key.startsWith("_") && key !== "student_image" && key !== "title"
            );

            // Match raw structural keys exactly against our computed top list
            const topFields = allEntries.filter(([rawKey]) => topSectionKeys.includes(rawKey));
            const bottomFields = allEntries.filter(([rawKey]) => !topSectionKeys.includes(rawKey));

            return (
              <div key={index} className="mt-2">
                
                {/* 1. TOP PARALLEL SECTION (Dynamic matching alongside picture window) */}
                <div className="row g-4 align-items-stretch">
                  <div className="col-8">
                    <div className="row g-2">
                      {topFields.map(([rawKey, value]) => {
                        const labelName = rawKey.split("~")[0];
                        return (
                          <div key={rawKey} className="col-12">
                            <div className="info-card" style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 12px",
                              backgroundColor: "#f8fafc",
                              borderRadius: "6px",
                              border: "1px solid #f1f5f9"
                            }}>
                              <div style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.75px" }}>
                                {labelName.replace(/_/g, ' ')}
                              </div>
                              <div style={{ fontSize: "13.5px", fontWeight: "600", color: "#0f172a" }}>
                                {value || "—"}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Passport Photo Compartment */}
                  <div className="col-4 d-flex justify-content-center align-items-center">
                    <div style={{
                      width: "40mm", 
                      height: "48mm", 
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "5px",
                      backgroundColor: "#fff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.04)"
                    }}>
                      <img
                        src={`${API_URL}/${header.student_image}`}
                        alt="Student Portrait"
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }}
                      />
                    </div>
                  </div>
                </div>

                {/* 2. BOTTOM FLUID SECTION (Everything else cascades at full width underneath) */}
                <div className="row g-2 mt-2">
                  {bottomFields.map(([rawKey, value]) => {
                    const [labelName, customCol] = rawKey.split("~");
                    const gridClass = customCol || "col-12";
                    const isSplitColumn = gridClass !== "col-12";

                    return (
                      <div key={rawKey} className={gridClass}>
                        <div className="info-card" style={{
                          display: "flex",
                          flexDirection: isSplitColumn ? "column" : "row",
                          justifyContent: isSplitColumn ? "flex-start" : "space-between",
                          alignItems: isSplitColumn ? "flex-start" : "center",
                          padding: "8px 12px",
                          backgroundColor: "#f8fafc",
                          borderRadius: "6px",
                          minHeight: isSplitColumn ? "58px" : "auto",
                          border: "1px solid #f1f5f9"
                        }}>
                          <div style={{
                            fontSize: "10px",
                            fontWeight: "700",
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.75px",
                            marginBottom: isSplitColumn ? "3px" : "0",
                            whiteSpace: "nowrap"
                          }}>
                            {labelName.replace(/_/g, ' ')}
                          </div>
                          <div style={{
                            fontSize: "13.5px",
                            fontWeight: "600",
                            color: "#0f172a",
                            textAlign: isSplitColumn ? "left" : "right"
                          }}>
                            {value || "—"}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Operational Note Block */}
                  <div 
                    className="col-12 mt-4" 
                    style={{ fontSize: "13px", lineHeight: "1.6",color:"black" }}
                    dangerouslySetInnerHTML={{ __html: info.message }} 
                  />
                </div>

              </div>
            );
          })}
        </div>

        {/* Footer Area */}
        <div>
          {/* Signatures Row */}
          <div className="row" style={{ marginTop: "40px", marginBottom: "25px" }}>
            
            {/* Guardian Authorization */}
            <div className="col-6">
              <div style={{ width: "85%", marginRight: "auto" }}>
                <p style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a", margin: "0 0 2px 0" }}>
                  {footer.guardian}
                </p>
                <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                  Saxiixa Masuulka / Guardian Signature
                </span>
                <div style={{ height: "50px" }}></div> 
                <div style={{ borderTop: "2px dashed #0f172a", width: "100%" }}></div>
              </div>
            </div>

            {/* Registrar Authority */}
            <div className="col-6">
              <div style={{ width: "85%", marginLeft: "auto" }}>
                <p style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a", margin: "0 0 2px 0", textTransform: "uppercase" }}>
                  {footer.registrar}
                </p>
                <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                  Saxiixa Diwaangeliyaha / Registrar Signature
                </span>
                <div style={{ height: "50px" }}></div> 
                <div style={{ borderTop: "2px dashed #0f172a", width: "100%" }}></div>
              </div>
            </div>

          </div>
   
          <div
            style={{
              borderTop: "1px solid #e2e8f0",
              paddingTop: "14px",
              textAlign: "center",
              fontSize: "11px",
              color: "#94a3b8",
              fontWeight: "600",
              letterSpacing: "0.75px",
              textTransform: "uppercase"
            }}
          >
            Developed By Kaafiye Technology Center (KTC)
          </div>
        </div>

      </div>
    </>
  );
};

export default CustomRegistrationLetter;